import { useState, useEffect } from 'react'

interface GeoPrice {
  price: string
  currency: string
  symbol: string
  loading: boolean
}

const PRICE_MAP: Record<
  string,
  { price: number; currency: string; symbol: string }
> = {
  MX: { price: 499, currency: 'MXN', symbol: '$' },
  CO: { price: 110000, currency: 'COP', symbol: '$' },
  AR: { price: 26000, currency: 'ARS', symbol: '$' },
  CL: { price: 25000, currency: 'CLP', symbol: '$' },
  PE: { price: 100, currency: 'PEN', symbol: 'S/' },
  ES: { price: 25, currency: 'EUR', symbol: '€' },
  BR: { price: 135, currency: 'BRL', symbol: 'R$' },
  GT: { price: 210, currency: 'GTQ', symbol: 'Q' },
  HN: { price: 670, currency: 'HNL', symbol: 'L' },
  CR: { price: 14000, currency: 'CRC', symbol: '₡' },
  DO: { price: 1600, currency: 'DOP', symbol: 'RD$' },
  EC: { price: 27, currency: 'USD', symbol: '$' },
  PA: { price: 27, currency: 'USD', symbol: '$' },
  UY: { price: 1100, currency: 'UYU', symbol: '$' },
  BO: { price: 190, currency: 'BOB', symbol: 'Bs' },
  PY: { price: 200000, currency: 'PYG', symbol: '₲' },
  VE: { price: 27, currency: 'USD', symbol: '$' },
  NI: { price: 990, currency: 'NIO', symbol: 'C$' },
  SV: { price: 27, currency: 'USD', symbol: '$' },
  US: { price: 27, currency: 'USD', symbol: '$' },
  GB: { price: 22, currency: 'GBP', symbol: '£' },
  FR: { price: 25, currency: 'EUR', symbol: '€' },
  DE: { price: 25, currency: 'EUR', symbol: '€' },
  IT: { price: 25, currency: 'EUR', symbol: '€' },
}

const DEFAULT_PRICE = { price: 27, currency: 'USD', symbol: '$' }

function formatPrice(price: number): string {
  if (price >= 1000) {
    return price.toLocaleString('es')
  }
  return price.toString()
}

export function useGeoPrice(): GeoPrice {
  const [geoPrice, setGeoPrice] = useState<GeoPrice>({
    price: `${DEFAULT_PRICE.symbol}${DEFAULT_PRICE.price} ${DEFAULT_PRICE.currency}`,
    currency: DEFAULT_PRICE.currency,
    symbol: DEFAULT_PRICE.symbol,
    loading: false,
  })

  useEffect(() => {
    const cached = sessionStorage.getItem('geo_price')
    if (cached) {
      try {
        setGeoPrice({ ...JSON.parse(cached), loading: false })
        return
      } catch {}
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 2000)

    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        const countryCode = data?.country_code || 'US'
        const priceData = PRICE_MAP[countryCode] || DEFAULT_PRICE
        const result = {
          price: `${priceData.symbol}${formatPrice(priceData.price)} ${priceData.currency}`,
          currency: priceData.currency,
          symbol: priceData.symbol,
          loading: false,
        }
        sessionStorage.setItem('geo_price', JSON.stringify(result))
        setGeoPrice(result)
      })
      .catch(() => {
        // Already showing default price, no state update needed
      })
      .finally(() => clearTimeout(timeout))

    return () => {
      controller.abort()
      clearTimeout(timeout)
    }
  }, [])

  return geoPrice
}
